/**
 * Phase 21.4 Batch 3C — 🏆 Admin · Contest editor (create/edit wizard)
 *
 * Single-page form with sections:
 *   1. Basics — title, slug (live-validated), description, banner
 *   2. Entry rules — entry_type (track|beat), submission_source, max_entries, dates
 *   3. Rewards — coins / promo_plays / badges per position (badges from the
 *      admin badge catalog picker)
 *
 * Save handler: POST /api/admin/contests (create) or PATCH .../{id} (edit).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import SharedNavigation from "@/components/SharedNavigation";
import BannerUploadField from "@/components/admin/BannerUploadField";
import DateTimeField from "@/components/admin/DateTimeField";
import api from "@/lib/api";
import { toast } from "sonner";

const ENTRY_TYPES = ["track", "beat"];
const SUBMISSION_SOURCES = ["existing", "new", "either"];
const POS_KEYS = ["1", "2", "3", "participation"];

const emptyContest = () => ({
  slug: "",
  title: "",
  description: "",
  rules_md: "",
  judging_criteria: [],
  banner_image: "",
  entry_type: "track",
  submission_source: "existing",
  max_entries_per_user: 1,
  winner_slots: 3,
  starts_at: "",
  ends_at: "",
  submission_deadline: "",
  results_at: "",
  rewards: {
    "1": { coins: 500, promo_plays: 5000, badges: [] },
    "2": { coins: 250, promo_plays: 2500, badges: [] },
    "3": { coins: 100, promo_plays: 1000, badges: [] },
    participation: { coins: 0, promo_plays: 0, badges: [] },
  },
  // iter_218 — Coin entry fee. Default 0 = Free entry. Existing
  // contests that don't set this stay free.
  entry_fee_coins: 0,
});

const AdminContestEditorPage = () => {
  const { contestId } = useParams();
  const isEdit = Boolean(contestId);
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyContest());
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [slugState, setSlugState] = useState({ status: "idle", available: null });
  const [badges, setBadges] = useState([]);
  // iter_218 — Sticky COIN mode independent of the numeric value so an
  // admin can type '0' or clear the field without the section
  // collapsing back to FREE. FREE mode still forces the persisted value
  // to 0 on save.
  const [coinMode, setCoinMode] = useState(false);
  const slugTimer = useRef(null);

  // iter_211 — Scroll to top on mount / route change so the breadcrumb,
  // title, and Back/Cancel/Save controls are always visible under the
  // fixed nav bar. Prevents the "editor opens mid-page" glitch when
  // the admin was scrolled deep in the list.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [contestId]);

  useEffect(() => {
    api.get("/admin/badges").then((r) => setBadges(r.data?.badges || [])).catch(() => setBadges([]));
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    api.get("/admin/contests").then((r) => {
      const found = (r.data?.contests || []).find((c) => c.contest_id === contestId);
      if (found) {
        setForm({
          ...emptyContest(),
          ...found,
          rewards: found.rewards || emptyContest().rewards,
        });
        // iter_218 — Reflect loaded fee state into the sticky coin-mode
        // toggle so admins see COIN mode when they open a paid contest.
        setCoinMode(Number(found.entry_fee_coins || 0) > 0);
      } else {
        toast.error("Contest not found");
      }
    }).catch(() => toast.error("Failed to load contest"))
      .finally(() => setLoading(false));
  }, [contestId, isEdit]);

  // Live slug validation — 400ms debounce.
  useEffect(() => {
    if (isEdit) return;   // slug cannot change once created (no PATCH support for slug)
    const s = form.slug?.trim();
    if (!s) {
      setSlugState({ status: "idle", available: null });
      return;
    }
    if (slugTimer.current) clearTimeout(slugTimer.current);
    setSlugState({ status: "checking", available: null });
    slugTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/admin/contests/check-slug`, { params: { slug: s } });
        setSlugState({
          status: "checked",
          available: !!r.data?.available,
          reason: r.data?.reason,
        });
      } catch {
        setSlugState({ status: "error", available: null });
      }
    }, 400);
  }, [form.slug, isEdit]);

  const canWinnerSlots = (n) => n === 1 || n === 3;

  const patchRewards = (pos, key, value) => {
    setForm((f) => ({
      ...f,
      rewards: {
        ...f.rewards,
        [pos]: { ...(f.rewards?.[pos] || {}), [key]: value },
      },
    }));
  };

  const toggleBadgeOnPos = (pos, badgeId) => {
    const current = (form.rewards?.[pos]?.badges) || [];
    const next = current.includes(badgeId)
      ? current.filter((b) => b !== badgeId)
      : [...current, badgeId];
    patchRewards(pos, "badges", next);
  };

  const save = async () => {
    if (!form.title?.trim() || !form.slug?.trim()) {
      toast.error("Title and slug are required");
      return;
    }
    if (!isEdit && slugState.available === false) {
      toast.error("Slug is not available — pick another");
      return;
    }
    if (!ENTRY_TYPES.includes(form.entry_type)) {
      toast.error("Entry type must be track or beat");
      return;
    }
    if (!canWinnerSlots(Number(form.winner_slots))) {
      toast.error("Winner slots must be 1 or 3");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        description: form.description || "",
        rules_md: form.rules_md || "",
        judging_criteria: form.judging_criteria || [],
        banner_image: form.banner_image || null,
        submission_source: form.submission_source,
        max_entries_per_user: Number(form.max_entries_per_user || 1),
        winner_slots: Number(form.winner_slots || 3),
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
        submission_deadline: form.submission_deadline || null,
        results_at: form.results_at || null,
        rewards: form.rewards,
        // iter_218 — Persist entry fee on both create + patch. FREE
        // mode forces 0 regardless of what's typed in the (now hidden)
        // amount input, so the visible mode is always the truth.
        entry_fee_coins: coinMode ? Math.max(0, Number(form.entry_fee_coins || 0)) : 0,
      };
      if (isEdit) {
        await api.patch(`/admin/contests/${contestId}`, payload);
        toast.success("Contest updated");
      } else {
        const create = {
          slug: form.slug,
          entry_type: form.entry_type,
          ...payload,
        };
        const r = await api.post("/admin/contests", create);
        toast.success("Contest created (status: draft)");
        navigate(`/admin/contests/${r.data.contest.contest_id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const availableBadgesForPos = useMemo(() => {
    // For contest badges, filter to badges that either have no
    // contest_id (generic) or match this contest.
    return badges.filter((b) => b.active && (!b.contest_id || b.contest_id === contestId));
  }, [badges, contestId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <div className="max-w-4xl mx-auto px-4 pt-24 pb-8">
          <button
            onClick={() => navigate("/admin/contests")}
            className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white"
            data-testid="admin-contest-editor-back-loading"
          >
            <span aria-hidden>←</span>
            <span>Back to Contest Admin</span>
          </button>
          <p className="p-4 text-gray-500 mt-4" data-testid="admin-contest-editor-loading">Loading…</p>
        </div>
      </div>
    );
  }

  const winnerPositions = Number(form.winner_slots) === 1 ? ["1"] : ["1", "2", "3"];
  const showParticipation = true;

  // iter_211 — Preserve list-view context (filter/search/scroll) on the
  // Contest Center list page when the admin returns via the Back button.
  const goBackToList = () => navigate("/admin/contests");
  const goToEntries = () => {
    if (contestId) navigate(`/admin/contests/${contestId}/entries`);
  };
  const cancel = () => {
    // Return without saving. Refresh-safe: browser back or explicit click
    // both land on the list with the persisted state intact.
    goBackToList();
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-contest-editor-page">
      <SharedNavigation />
      <div className="max-w-4xl mx-auto px-4 pt-24 pb-8 space-y-6">

        {/* iter_211 — Breadcrumb + primary nav. Always shows the Back
            link and (when editing) a shortcut to the entries page.
            Sticky-top so mobile users don't have to scroll up. */}
        <div
          className="flex flex-wrap items-center gap-3 text-xs"
          data-testid="admin-contest-editor-breadcrumb"
        >
          <button
            onClick={goBackToList}
            className="inline-flex items-center gap-1 text-gray-300 hover:text-white transition-colors"
            data-testid="admin-contest-editor-back"
          >
            <span aria-hidden>←</span>
            <span>Back to Contest Admin</span>
          </button>
          <span className="text-gray-600">/</span>
          <span className="text-gray-500 truncate max-w-[50vw]">
            {isEdit ? (form.title || "Untitled contest") : "New contest"}
          </span>
          {isEdit && (
            <>
              <span className="text-gray-600">·</span>
              <button
                onClick={goToEntries}
                className="text-cyan-300 hover:text-cyan-200 underline"
                data-testid="admin-contest-editor-open-entries"
              >
                View entries →
              </button>
              <span className="text-gray-600">·</span>
              <button
                onClick={() => navigate(`/admin/contests/${contestId}/judging`)}
                className="text-fuchsia-300 hover:text-fuchsia-200 underline"
                data-testid="admin-contest-editor-open-judging"
              >
                Judging →
              </button>
            </>
          )}
        </div>

        <div className="flex flex-wrap justify-between items-center gap-3">
          <h1 className="text-3xl font-black">{isEdit ? "Edit contest" : "New contest"}</h1>
          <div className="flex items-center gap-2">
            <Button
              onClick={cancel}
              variant="outline"
              className="border-white/20 text-gray-200 hover:bg-white/5 font-black"
              data-testid="admin-contest-editor-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-white text-black hover:bg-amber-200 font-black"
              data-testid="admin-contest-editor-save"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create draft"}
            </Button>
          </div>
        </div>

        {/* 1. BASICS */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4" data-testid="editor-basics">
          <h2 className="text-lg font-black">1 · Basics</h2>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-400">Title *</Label>
            <Input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-white/5 border-white/10 text-white"
              data-testid="editor-title"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-400">
              Slug * {isEdit && <span className="normal-case text-gray-500">(read-only)</span>}
            </Label>
            <Input
              value={form.slug}
              disabled={isEdit}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })}
              placeholder="summer-flow-2026"
              className="bg-white/5 border-white/10 text-white font-mono"
              data-testid="editor-slug"
            />
            {!isEdit && form.slug && (
              <p className="text-xs mt-1" data-testid="editor-slug-status">
                {slugState.status === "checking" && <span className="text-gray-400">Checking availability…</span>}
                {slugState.status === "checked" && slugState.available && (
                  <span className="text-emerald-400">✓ Slug is available</span>
                )}
                {slugState.status === "checked" && !slugState.available && (
                  <span className="text-rose-400">✗ Slug not available{slugState.reason ? ` (${slugState.reason})` : ""}</span>
                )}
                {slugState.status === "error" && <span className="text-rose-400">Check failed — try again</span>}
              </p>
            )}
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-400">Description</Label>
            <Textarea
              value={form.description || ""}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="bg-white/5 border-white/10 text-white min-h-[80px]"
              data-testid="editor-description"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-400">Rules (markdown)</Label>
            <Textarea
              value={form.rules_md || ""}
              onChange={(e) => setForm({ ...form, rules_md: e.target.value })}
              className="bg-white/5 border-white/10 text-white min-h-[100px] font-mono text-xs"
              data-testid="editor-rules"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-400 mb-1 block">Banner image</Label>
            <BannerUploadField
              value={form.banner_image || ""}
              onChange={(url) => setForm({ ...form, banner_image: url })}
              purpose="banner"
              testIdPrefix="editor-banner"
              label=""
            />
          </div>
        </section>

        {/* 2. ENTRY RULES */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4" data-testid="editor-entry-rules">
          <h2 className="text-lg font-black">2 · Entry rules</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-400">Entry type *</Label>
              <select
                value={form.entry_type}
                disabled={isEdit}
                onChange={(e) => setForm({ ...form, entry_type: e.target.value })}
                className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                data-testid="editor-entry-type"
              >
                {ENTRY_TYPES.map((t) => (
                  <option key={t} value={t} className="bg-[#13131a]">{t}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-400">Submission source</Label>
              <select
                value={form.submission_source}
                onChange={(e) => setForm({ ...form, submission_source: e.target.value })}
                className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                data-testid="editor-submission-source"
              >
                {SUBMISSION_SOURCES.map((s) => (
                  <option key={s} value={s} className="bg-[#13131a]">{s}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-400">Max entries / user</Label>
              <Input
                type="number"
                min={1}
                value={form.max_entries_per_user}
                onChange={(e) => setForm({ ...form, max_entries_per_user: Number(e.target.value) })}
                className="bg-white/5 border-white/10 text-white"
                data-testid="editor-max-entries"
              />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-gray-400">Winner slots</Label>
              <select
                value={form.winner_slots}
                onChange={(e) => setForm({ ...form, winner_slots: Number(e.target.value) })}
                className="mt-1 w-full h-9 rounded-md bg-white/5 border border-white/10 text-white px-2 text-sm"
                data-testid="editor-winner-slots"
              >
                <option value={1} className="bg-[#13131a]">1 (single winner)</option>
                <option value={3} className="bg-[#13131a]">3 (1st / 2nd / 3rd)</option>
              </select>
            </div>
            <DateTimeField
              value={form.submission_deadline}
              onChange={(iso) => setForm({ ...form, submission_deadline: iso })}
              testId="editor-deadline"
              label="Submission Deadline"
              required
              hint="The exact moment entries close — countdown clock target."
            />
          </div>

          {/* Full contest schedule — Start / End / Results */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <DateTimeField
              value={form.starts_at}
              onChange={(iso) => setForm({ ...form, starts_at: iso })}
              testId="editor-starts-at"
              label="Start Date"
              hint="Before this, contest shows 'Coming Soon'."
            />
            <DateTimeField
              value={form.ends_at}
              onChange={(iso) => setForm({ ...form, ends_at: iso })}
              testId="editor-ends-at"
              label="Contest End Date"
              hint="Overall end date (informational, separate from deadline)."
            />
            <DateTimeField
              value={form.results_at}
              onChange={(iso) => setForm({ ...form, results_at: iso })}
              testId="editor-results-at"
              label="Results Date"
              hint="When winners are expected/published."
            />
          </div>
        </section>

        {/* iter_218 — Entry Fee. Uses existing coin-paid backend from
            iter_206/208: coins are debited atomically before the entry
            row is inserted (paid_balance first, then reward_balance).
            Existing contests default to 0 = Free until admin bumps. */}
        <section
          className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 space-y-3"
          data-testid="editor-entry-fee"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-black">🪙 Entry Fee</h2>
              <p className="text-xs text-gray-400">
                Charge RGM Coins to enter this contest. Fees deduct
                atomically at entry time and refund with{" "}
                <code className="text-amber-200">POST /admin/contests/{"{id}"}/entries/{"{eid}"}/refund-fee</code>.
              </p>
            </div>
            <span
              className={`text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full border ${
                coinMode
                  ? "bg-amber-500/20 text-amber-200 border-amber-500/40"
                  : "bg-emerald-500/20 text-emerald-200 border-emerald-500/40"
              }`}
              data-testid="editor-entry-fee-pill"
            >
              {coinMode
                ? `🪙 ${Math.max(0, Number(form.entry_fee_coins || 0))} per entry`
                : "Free entry"}
            </span>
          </div>

          <div
            className="grid grid-cols-2 gap-2"
            role="radiogroup"
            aria-label="Entry fee type"
          >
            <button
              type="button"
              onClick={() => setCoinMode(false)}
              className={`text-left rounded-lg border px-3 py-2 transition-colors ${
                !coinMode
                  ? "border-emerald-500/60 bg-emerald-500/10"
                  : "border-white/10 bg-white/[0.03] hover:border-white/20"
              }`}
              data-testid="editor-entry-fee-free"
              aria-pressed={!coinMode}
            >
              <p className="text-white font-black text-sm">Free entry</p>
              <p className="text-[11px] text-gray-400">0 coins required.</p>
            </button>
            <button
              type="button"
              onClick={() => {
                setCoinMode(true);
                // Seed a sensible default only if amount is 0 so a
                // returning admin doesn't get their previous value
                // wiped.
                if (Number(form.entry_fee_coins || 0) <= 0) {
                  setForm({ ...form, entry_fee_coins: 50 });
                }
              }}
              className={`text-left rounded-lg border px-3 py-2 transition-colors ${
                coinMode
                  ? "border-amber-500/60 bg-amber-500/10"
                  : "border-white/10 bg-white/[0.03] hover:border-white/20"
              }`}
              data-testid="editor-entry-fee-coin"
              aria-pressed={coinMode}
            >
              <p className="text-white font-black text-sm">🪙 Coin entry</p>
              <p className="text-[11px] text-gray-400">Charge coins per entry.</p>
            </button>
          </div>

          {coinMode && (
            <div data-testid="editor-entry-fee-amount-wrap">
              <Label className="text-[11px] uppercase tracking-wider text-gray-400">
                Coins per entry
              </Label>
              <Input
                type="number"
                min={1}
                step={1}
                value={form.entry_fee_coins}
                onChange={(e) => setForm({
                  ...form,
                  entry_fee_coins: Math.max(0, Number(e.target.value) || 0),
                })}
                className="bg-black/30 border-white/10 text-white"
                data-testid="editor-entry-fee-amount"
              />
              <p className="text-[11px] text-amber-200/80 mt-1">
                Artists will see{" "}
                <span className="font-black">🪙 {Math.max(0, Number(form.entry_fee_coins || 0))} required</span>{" "}
                on the Enter Contest screen. Insufficient-balance returns 402;
                second attempts by the same artist return 409.
              </p>
            </div>
          )}
        </section>

        {/* 3. REWARDS */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4" data-testid="editor-rewards">
          <h2 className="text-lg font-black">3 · Rewards</h2>
          <p className="text-xs text-gray-400">
            Contest coins land in <span className="text-emerald-300 font-black">reward_balance</span> ONLY — never withdrawable to cash via Stripe.
          </p>
          {winnerPositions.map((pos) => (
            <div
              key={pos}
              className="rounded-lg border border-white/10 p-3 bg-black/40"
              data-testid={`editor-rewards-pos-${pos}`}
            >
              <p className="text-sm font-black text-white mb-2">Position {pos}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-gray-400">Reward coins</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.rewards?.[pos]?.coins ?? 0}
                    onChange={(e) => patchRewards(pos, "coins", Number(e.target.value))}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid={`editor-rewards-coins-${pos}`}
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-gray-400">Promotional plays (track only)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.rewards?.[pos]?.promo_plays ?? 0}
                    onChange={(e) => patchRewards(pos, "promo_plays", Number(e.target.value))}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid={`editor-rewards-promo-${pos}`}
                  />
                </div>
              </div>
              <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">Badges (pick from catalog)</p>
              <div className="flex flex-wrap gap-1.5" data-testid={`editor-badges-pos-${pos}`}>
                {availableBadgesForPos.length === 0 ? (
                  <span className="text-xs text-gray-500">No badges — go to Badge Catalog to add.</span>
                ) : (
                  availableBadgesForPos.map((b) => {
                    const on = (form.rewards?.[pos]?.badges || []).includes(b.badge_id);
                    return (
                      <button
                        key={b.badge_id}
                        type="button"
                        onClick={() => toggleBadgeOnPos(pos, b.badge_id)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-black transition-colors border ${on ? "bg-amber-500/20 border-amber-500/60 text-amber-100" : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"}`}
                        data-testid={`editor-badge-toggle-${pos}-${b.badge_id}`}
                      >
                        {b.label}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          ))}

          {showParticipation && (
            <div
              className="rounded-lg border border-white/10 p-3 bg-black/40"
              data-testid="editor-rewards-pos-participation"
            >
              <p className="text-sm font-black text-white mb-2">Participation (all entrants)</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-gray-400">Coins</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.rewards?.participation?.coins ?? 0}
                    onChange={(e) => patchRewards("participation", "coins", Number(e.target.value))}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="editor-rewards-coins-participation"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-gray-400">Promo plays (track only)</Label>
                  <Input
                    type="number"
                    min={0}
                    value={form.rewards?.participation?.promo_plays ?? 0}
                    onChange={(e) => patchRewards("participation", "promo_plays", Number(e.target.value))}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="editor-rewards-promo-participation"
                  />
                </div>
              </div>
            </div>
          )}
        </section>

        <div className="flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-white text-black hover:bg-amber-200 font-black"
            data-testid="admin-contest-editor-save-bottom"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create draft"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminContestEditorPage;
